
import React from "react";
import { formatCurrency } from '../../utils/calculator';

interface ContributionCapExplanationProps {
  weeklyPaymentWithLoan: number;
  nursingHomeCost: number;
  actualWeeklyPayment: number;
}

export function ContributionCapExplanation({
  weeklyPaymentWithLoan,
  nursingHomeCost,
  actualWeeklyPayment
}: ContributionCapExplanationProps) {
  const isCapped = weeklyPaymentWithLoan > nursingHomeCost;
  const cappedWeeklyPayment = Math.min(weeklyPaymentWithLoan, nursingHomeCost);
  const stateSupportAmount = nursingHomeCost - cappedWeeklyPayment;

  return (
    <div className="bg-white p-6 rounded-lg border shadow-sm">
      <h4 className="font-semibold mb-4 text-gray-900 text-lg">Understanding Your Fair Deal Payment</h4>
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600 mb-1">Nursing Home Cost</p>
            <p className="text-xl font-semibold text-gray-900">{formatCurrency(nursingHomeCost)}/week</p>
            <p className="text-xs text-gray-500 mt-1">The total weekly cost of care</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600 mb-1">Your Means Assessment</p>
            <p className={`text-xl font-semibold ${isCapped ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
              {formatCurrency(weeklyPaymentWithLoan)}/week
            </p>
            <p className="text-xs text-gray-500 mt-1">Based on your income and assets</p>
          </div>
          <div className="p-4 bg-green-50 rounded-md border border-green-200">
            <p className="text-sm text-gray-600 mb-1">Your Actual Payment</p>
            <p className="text-xl font-semibold text-grandpal-green">{formatCurrency(cappedWeeklyPayment)}/week</p>
            <p className="text-xs text-green-700 mt-1">What you actually pay under Fair Deal</p>
          </div>
        </div>

        {isCapped && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1 bg-green-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h5 className="font-semibold text-green-800">Your payment is capped</h5>
            </div>
            <p className="text-sm text-green-700">
              Your means assessment result ({formatCurrency(weeklyPaymentWithLoan)}/week) exceeds the nursing home cost.
              Under Fair Deal rules, your payment is capped at the nursing home cost, saving you <strong>{formatCurrency(weeklyPaymentWithLoan - nursingHomeCost)}/week</strong>.
            </p>
          </div>
        )}
        
        {stateSupportAmount > 0 && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1 bg-blue-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              </div>
              <h5 className="font-semibold text-blue-800">State Support</h5>
            </div>
            <p className="text-sm text-blue-700 mb-2">
              The Fair Deal scheme provides state support of <strong>{formatCurrency(stateSupportAmount)}/week</strong> to cover the gap between your contribution and the total nursing home cost.
            </p>
            <div className="flex items-center justify-between text-blue-700 font-medium">
              <span>Total Nursing Home Cost:</span>
              <span>{formatCurrency(nursingHomeCost)}/week</span>
            </div>
            <div className="flex items-center justify-between text-blue-700 font-medium">
              <span>Your Contribution:</span>
              <span>- {formatCurrency(cappedWeeklyPayment)}/week</span>
            </div>
            <div className="border-t border-blue-200 mt-2 pt-2 flex items-center justify-between text-blue-800 font-semibold">
              <span>State Support:</span>
              <span>{formatCurrency(stateSupportAmount)}/week</span>
            </div>
          </div>
        )}
        
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h5 className="text-sm font-semibold text-blue-800 mb-2">How Fair Deal Works</h5>
          <ul className="list-disc pl-5 text-sm text-blue-700 space-y-2">
            <li>Your means assessment looks at your income and assets to calculate a contribution</li>
            <li><strong>The actual amount you pay is capped at the nursing home's weekly fee</strong></li>
            <li>You'll never pay more than {formatCurrency(nursingHomeCost)}/week, even if your means assessment is higher</li>
            <li>The State covers any difference between your contribution and the nursing home cost</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
